// Pure data helpers for the Game Props page (GameProps.tsx): odds
// formatting, section/type grouping, trigger matching and player-prop
// labelling. Kept free of React so they can be unit tested directly.
import {
  toTitleLabel,
  COMPOUND_GROUPS,
  BASE_TO_COMPOUND,
  PREFIX_GROUPS_DISPLAY,
  type CompoundMember,
} from "./gamePropsConfig";

export interface PropBet {
  player_name: string;
  prop_type: string;
  line: number | string;
  over_odds: number | string;
  under_odds: number | string;
  [key: string]: unknown;
}

// response["event"]["odds"][sportsbook] => { field: value, ... }  (2-level)
export type OddsEntry = Record<string, unknown>;
export type SimpleOddsMap = Record<string, OddsEntry>;

// response["event"]["game_odds" | "player_odds"][sportsbook][odds_type] => { field: value, ... }  (3-level)
export type SportsbookOdds = Record<string, OddsEntry>;
export type GameOddsMap = Record<string, SportsbookOdds>;

export interface EventData {
  odds?: SimpleOddsMap;
  game_odds?: GameOddsMap;
  player_odds?: GameOddsMap;
  // Pre-game snapshots — the live maps above are empty right after a game's
  // start time passes but before the live feed has produced data yet.
  starting_odds?: SimpleOddsMap;
  starting_game_odds?: GameOddsMap;
  starting_player_odds?: GameOddsMap;
  home_team?: string;
  away_team?: string;
  sport?: string;
  home_score?: number;
  away_score?: number;
  start_time?: string | number;
  game_status?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PropsData {
  event?: EventData;
  game_id?: number;
  home_team?: string;
  away_team?: string;
  sport?: string;
  props?: PropBet[];
  [key: string]: unknown;
}

// A rule that has hit for this game. Keyed in the API response by game id
// (matching the "triggers" shape used on the active_games page); for a
// team/game-level prop, prop_key names the raw game_odds field. For a player
// prop, player_id + prop_stem identify the player and the stat field instead.
export interface PropTrigger {
  title: string;
  details: string;
  rule_id: number;
  rule_definition_id: number;
  prop_key: string | null;
  player_id: string | null;
  prop_stem: string | null;
}
// ── Helpers ──────────────────────────────────────────────────────────────────

export function formatOdds(val: unknown): string {
  if (val === null || val === undefined || val === "") return "-";
  // An array/object (e.g. an unhandled "_lines" alt-line list) has no
  // sensible single-value display — stringifying it would print
  // "[object Object]" per entry instead of failing quietly.
  if (typeof val === "object") return "-";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return n > 0 ? `+${n}` : String(n);
}


export const HIDDEN_ODDS_TYPES = new Set([
  "regulation_spread",
  "reg_away_has_points",
  "reg_away_no_points",
  "reg_both_has_scores",
  "reg_both_no_scores",
  "reg_home_has_points",
  "reg_home_no_points",
]);

export const HIDDEN_ODDS_PREFIXES = ["match_result_btts"];

export const GAME_ODDS_LABEL_OVERRIDES: Record<string, string> = {};

// Sport-specific display renames for Game Odds sections, keyed by the section's
// type (compound group key, or the raw base for ungrouped types).
export type LabelOverrides = Record<string, string>;

export const MLS_LABEL_OVERRIDES: LabelOverrides = {
  over_total_shots_on_target: "Total Shots On Target",
  reg_points_even_odd: "Points Odd/Even",
  reg_has_points: "Regulation Has Points",
  reg_no_points: "Regulation No Points",
  away_corner_kicks_line: "Team Corners",
};

// Per-book member selection for a compound group: given a book's odds and the
// group's members, returns the members that apply to that book (default: all).
// Lets two groups split one set of ambiguous keys by what each book offers.
export type MemberResolvers = Record<
  string,
  (sbOdds: OddsEntry, members: CompoundMember[]) => CompoundMember[]
>;

export const hasFirstHalfDraw = (sbOdds: OddsEntry) =>
  Object.keys(sbOdds).some((k) => k.startsWith("first_half_draw_outcome"));

// NFL/NCAAF 1st-half moneylines come in both 2-way and 3-way flavors, and books
// reuse "first_half_away/home_outcome" for both. Split by book: a book that
// offers a draw price is 3-way; a book without one is 2-way (its away/home
// outcome keys join the 2-way group alongside "away/home_first_half_outcome").
export const FOOTBALL_MEMBER_RESOLVERS: MemberResolvers = {
  h1_moneyline: (sbOdds, members) => (hasFirstHalfDraw(sbOdds) ? members : []),
  h1_moneyline_alt: (sbOdds, members) =>
    hasFirstHalfDraw(sbOdds)
      ? members
      : [
          ...members,
          { base: "first_half_away_outcome", rowLabel: "Away" },
          { base: "first_half_home_outcome", rowLabel: "Home" },
        ],
};

// NBA quarter moneylines: books post both a 2-way and a 3-way market for every
// quarter, and the backend keeps them apart by key format, same as the 1st
// half's h1_moneyline/h1_moneyline_alt: team-first
// ("away_first_quarter_outcome") is the 2-way market, quarter-first
// ("first_quarter_away_outcome") plus "first_quarter_draw_outcome" is the
// 3-way one. The shared q{N}_moneyline/q{N}_moneyline_2way groups are laid out
// the other way round for football, so for NBA each group's members are swapped
// to the right key format (the draw stays with the 3-way group).
export const NBA_MEMBER_RESOLVERS: MemberResolvers = {};
// Groups that must be listed together: a key's format puts it in one group, but
// the resolvers above show it in the other.
export const NBA_LINKED_TYPES: string[][] = [];
export const NBA_TRIGGER_TYPE_OVERRIDES: Record<string, string> = {};
["first", "second", "third", "fourth"].forEach((quarter, i) => {
  const q = `q${i + 1}`;
  const outcomeMembers = (format: "team_first" | "quarter_first") =>
    (["away", "home"] as const).map((side) => ({
      base:
        format === "team_first"
          ? `${side}_${quarter}_quarter_outcome`
          : `${quarter}_quarter_${side}_outcome`,
      rowLabel: side === "away" ? "Away" : "Home",
    }));
  const drawMember = {
    base: `${quarter}_quarter_draw_outcome`,
    rowLabel: "Draw",
  };
  NBA_MEMBER_RESOLVERS[`${q}_moneyline`] = () => {
    const [away, home] = outcomeMembers("quarter_first");
    return [away, drawMember, home];
  };
  NBA_MEMBER_RESOLVERS[`${q}_moneyline_2way`] = () =>
    outcomeMembers("team_first");
  NBA_LINKED_TYPES.push([`${q}_moneyline`, `${q}_moneyline_2way`]);
  // An alert on a quarter moneyline rule highlights the group its key is shown
  // in above, not the one BASE_TO_COMPOUND files the key under.
  outcomeMembers("team_first").forEach(({ base }) => {
    NBA_TRIGGER_TYPE_OVERRIDES[base] = `${q}_moneyline_2way`;
  });
  outcomeMembers("quarter_first").forEach(({ base }) => {
    NBA_TRIGGER_TYPE_OVERRIDES[base] = `${q}_moneyline`;
  });
});

export function getOddsTypeDisplayLabel(
  t: string,
  overrides?: LabelOverrides,
): string {
  if (overrides?.[t]) return overrides[t];
  if (GAME_ODDS_LABEL_OVERRIDES[t]) return GAME_ODDS_LABEL_OVERRIDES[t];
  for (const { prefix, label } of Object.values(PREFIX_GROUPS_DISPLAY)) {
    if (t.startsWith(prefix)) return label;
  }
  if (COMPOUND_GROUPS[t]) return COMPOUND_GROUPS[t].label;
  return toTitleLabel(t);
}

// Same base→compound-group normalization collectTypes uses, exposed so a
// trigger's raw prop_key can be resolved to the "oddsType" a section is keyed by.
export function normalizeToOddsType(key: string): string {
  const base = key.replace(/_point$|_price$|_lines$/, "");
  return BASE_TO_COMPOUND[base] ?? base;
}

// Sport-specific merges of one compound group into another (source → target):
// the source group's members are displayed as extra members of the target's
// section, and the source no longer gets a section of its own.
export type MergeTypes = Record<string, string>;

// MLB reports total runs under both "total_points_even/odd" and
// "total_runs_odd/even" depending on the book — show them as one market.
export const MLB_MERGE_TYPES: MergeTypes = {
  total_points_even: "total_runs_even_odd",
};

// Per-sport override of which section a trigger's prop_key highlights, keyed by
// the key's base (prop_key minus _point/_price/_lines) - for groups whose member
// resolvers show a key somewhere other than its static BASE_TO_COMPOUND group.
export type TriggerTypeOverrides = Record<string, string>;

export function triggersForOddsType(
  triggers: PropTrigger[],
  oddsType: string,
  mergeTypes?: MergeTypes,
  triggerTypeOverrides?: TriggerTypeOverrides,
): PropTrigger[] {
  return triggers.filter((t) => {
    if (t.player_id || !t.prop_key) return false;
    const base = t.prop_key.replace(/_point$|_price$|_lines$/, "");
    const type = triggerTypeOverrides?.[base] ?? normalizeToOddsType(t.prop_key);
    return (mergeTypes?.[type] ?? type) === oddsType;
  });
}

export function collectTypes(oddsMap: GameOddsMap, mergeTypes?: MergeTypes): string[] {
  const seen = new Set<string>();
  const types: string[] = [];
  Object.values(oddsMap).forEach((sbOdds) => {
    Object.keys(sbOdds).forEach((field) => {
      // Strip _point, _price, or _lines to get the base type key
      const base = field.replace(/_point$|_price$|_lines$/, "");
      // Map to compound group key if applicable, otherwise use the base
      const compoundKey = BASE_TO_COMPOUND[base] ?? base;
      const key = mergeTypes?.[compoundKey] ?? compoundKey;
      const hidden =
        HIDDEN_ODDS_TYPES.has(key) ||
        HIDDEN_ODDS_PREFIXES.some((prefix) => key.startsWith(prefix));
      if (!seen.has(key) && !hidden) {
        seen.add(key);
        types.push(key);
      }
    });
  });
  return types;
}

// Processes a flat odds entry into paired/labelled display fields.
export function processOddsEntry(entry: OddsEntry): SubgroupField[] {
  const processed = new Set<string>();
  const results: SubgroupField[] = [];

  Object.keys(entry).forEach((f) => {
    if (processed.has(f)) return;

    if (f.endsWith("_point") || f.endsWith("_price")) {
      const base = f.replace(/_point$|_price$/, "");
      const pointKey = `${base}_point`;
      const priceKey = `${base}_price`;
      processed.add(pointKey);
      processed.add(priceKey);

      const parts: string[] = [];
      const p = formatPoint(entry[pointKey]);
      if (p) parts.push(p);
      const pr = formatOdds(entry[priceKey]);
      if (pr !== "-") parts.push(`(${pr})`);
      if (parts.length === 0) return;

      const label = toTitleLabel(
        base
          .replace(/_total$/, "")
          .replace(/_spread$/, "")
          .replace(/_handicap$/, "")
          .replace(/_line$/, ""),
      );
      results.push({ label, display: parts.join(" "), key: base });
    } else if (f.toLowerCase().includes("outcome")) {
      processed.add(f);
      const val = entry[f];
      if (val === null || val === undefined || val === "") return;
      results.push({
        label: toTitleLabel(f.split("_outcome")[0]),
        display: formatOdds(val),
        key: f,
      });
    } else {
      processed.add(f);
      const val = entry[f];
      if (val === null || val === undefined || val === "") return;
      results.push({
        label: toTitleLabel(f),
        display: formatOdds(val),
        key: f,
      });
    }
  });

  return results;
}

// The chart page fetches by exact key (e.g. "away_spread_point"), so a bare
// member/group base like "away_spread" isn't itself fetchable — it needs
// whichever suffix the live data actually uses appended. Guessing a fixed
// suffix broke down for groups that only have "_price" (or, rarely, no
// suffix at all), silently redirecting the chart to an unrelated default key.
export function suffixedKeyIfPresent(base: string, sbOdds: OddsEntry): string | null {
  if (`${base}_point` in sbOdds) return `${base}_point`;
  if (`${base}_price` in sbOdds) return `${base}_price`;
  if (base in sbOdds) return base;
  return null;
}

export function resolveSuffixedKey(
  base: string,
  sbOdds: OddsEntry | undefined,
): string {
  return (sbOdds && suffixedKeyIfPresent(base, sbOdds)) || `${base}_point`;
}

// Picks whichever compound-group member actually has data (preferring the
// "Over" member, then declaration order) — the "books" list for a compound
// group is filtered by ANY member having data, so the preferred member (e.g.
// the first one, or "Over") isn't guaranteed to be the one that's actually
// present. Falling back to a member with no real data anywhere reproduced
// the same "jumps to an unrelated key" bug this was meant to fix.
export function resolveCompoundChartKey(
  members: CompoundMember[],
  books: [string, OddsEntry][],
): string {
  const overMember = members.find((m) => m.rowLabel === "Over");
  const ordered = overMember
    ? [overMember, ...members.filter((m) => m !== overMember)]
    : members;
  for (const member of ordered) {
    for (const [, sbOdds] of books) {
      const found = suffixedKeyIfPresent(member.base, sbOdds);
      if (found) return found;
    }
  }
  return `${ordered[0].base}_point`;
}

export const PLAYER_SECTION_LABELS: Record<string, string> = {
  //  player_batting_basesOnBalls: "Batting Walks",
  player_batting_doubles: "Doubles",
  player_batting_hits: "Hits",
  player_batting_home_runs: "Home Runs",
  player_batting_rbi: "RBIs",
  player_batting_singles: "Singles",
  player_batting_stolen_bases: "Stolen Bases",
  "player_batting_hits+runs+rbi": "Hits+Runs+RBIs",
  player_batting_triples: "Triples",
  player_batting_total_bases: "Total Bases",
  player_batting_strikeouts: "Strikeouts",
  player_strikeouts: "Strikeouts",
  player_pitching_bases_on_balls: "Bases On Balls",
  player_pitching_earned_runs: "Earned Runs",
  player_pitching_hits: "Hits",
  player_pitching_outs: "Outs",
  player_pitching_pitches_thrown: "Pitches Thrown",
  player_pitching_strikeouts: "Strikeouts",
  player_first_to_score: "First to Score",
  player_clearances: "Clearances",
  player_goalie_saves: "Goalie Saves Over/Under",
  player_passes_attempted: "Passes Attempted",
  player_shots_assisted: "Shots Assisted",
  player_shots_on_goal: "Shots on Goal",
  player_shots: "Shots",
  player_tackles: "Tackles",
  player_goals: "Goals",
  player_assists: "Record an Assist",
  player_shots_in_each_half: "At Least One Shot In Each Half",
  player_shots_on_target_in_each_half:
    "At Least One Shot On Target In Each Half",
  player_to_score_or_assist: "At Least One Score Or Assist",
  player_points: "Runs",
  player_rushing_receiving_yards: "Rushing and Receiving Yards",
  most_points_player_price: "Most Points",
  first_touchdown_scorer_price: "First Touchdown Scorer",
  last_touchdown_scorer_price: "Last Touchdown Scorer",
  anytime_touchdown_scorer_price: "Anytime Touchdown Scorer",
  player_p1_points: "1st Period Goals",
  // "assissts" is how the feed may spell it; the correct spelling is kept too.
  player_any_assissts_price: "Any Assists",
  player_any_assists_price: "Any Assists",
  player_any_rebounds_price: "Any Rebounds",
  player_blocks_steals: "Blocks + Steals",
  // Same as above: the feed may spell it "backet".
  player_first_backet_price: "First Basket",
  player_first_basket_price: "First Basket",
  player_first_half_points_rebounds_assists:
    "First Half Points + Rebounds + Assists",
  player_points_assists: "Points + Assists",
  player_points_rebounds: "Points + Rebounds",
  player_points_rebounds_assists: "Points + Rebounds + Assists",
  player_rebounds_assists: "Rebounds + Assists",
};

export const PLAYER_SECTION_ALIASES: Record<string, string> = {
  player_batting_strikeouts: "player_strikeouts",
  // Same market (pitcher strikeouts thrown), named differently by different
  // sportsbooks — merge them so they render as one "Strikeouts" group.
  player_pitching_strikeouts: "player_strikeouts",
};

export const PLAYER_PREFIX_GROUPS: { label: string; prefix: string }[] = [
  { label: "Batting", prefix: "player_batting_" },
  { label: "Batting", prefix: "player_points" },
  // player_strikeouts (and its player_batting_strikeouts alias) is a
  // pitcher's strikeouts-thrown prop, not a batter's own strikeouts.
  { label: "Pitching", prefix: "player_strikeouts" },
  { label: "Pitching", prefix: "player_pitching_" },
];

export function getPlayerSectionGroup(
  sectionName: string,
  isNfl: boolean,
  isNhl: boolean,
  isNba = false,
): string | null {
  // Batting/Pitching are baseball groups — NBA sections all sit at the top level.
  if (isNba) return null;
  // "player_points" is a baseball "Runs" stat (grouped under Batting), but the
  // same field name is reused by the NFL/NHL feeds for a different, standalone
  // stat — don't fold it into the baseball Batting group there.
  if ((isNfl || isNhl) && sectionName.startsWith("player_points")) return null;
  for (const { label, prefix } of PLAYER_PREFIX_GROUPS) {
    if (sectionName.startsWith(prefix)) return label;
  }
  return null;
}

export function getPlayerStatLabel(
  baseName: string,
  isNfl: boolean,
  isNhl: boolean,
  kind: "line" | "simple",
  isMls = false,
  isNba = false,
): string {
  if (isNba) {
    // PLAYER_SECTION_LABELS maps player_points to baseball "Runs". Exact match
    // only, so combo stats (e.g. player_points_rebounds) keep their own names.
    if (baseName === "player_points") return "Points";
    if (kind === "line" && baseName === "player_assists") return "Assists";
  }
  if (isMls) {
    if (kind === "line" && baseName.startsWith("player_assists"))
      return "Assist Over/Under";
    if (baseName === "player_goals") return "Goals Over/Under";
    if (baseName === "player_shots") return "Shots Over/Under";
  }
  if (isNfl && baseName.startsWith("player_points")) return "Player Points";
  // Only football offers this market, so no sport check; "includes" also covers
  // a "player_" prefixed key, which would otherwise fall through to a title-cased label.
  if (baseName.includes("anytime_touchdown_scorer")) return "Touchdown Scorer";
  // Football-only stat (NFL and NCAAF), so likewise no sport check.
  if (baseName.includes("passing_rushing_yards"))
    return "Passing and Rushing Yards";
  if (isNhl && baseName.startsWith("player_points"))
    return kind === "line" ? "Goals Over/Under" : "Goals (Yes)";
  if (baseName === "player_scores") return "Goals (Yes)";
  // These stats are offered both as an over/under line and as a flat yes
  // market — same base name, so distinguish the two by section kind.
  if (baseName.startsWith("player_shots_on_goal"))
    return kind === "line" ? "Shots on Goal Over/Under" : "Shots on Goal (Yes)";
  if (
    baseName.startsWith("player_goals_and_assist") ||
    baseName.startsWith("player_goals_assist")
  )
    return kind === "line"
      ? "Goals and Assists Over/Under"
      : "Goals and Assists (Yes)";
  if (baseName.startsWith("player_assists"))
    return kind === "line"
      ? "Record an Assist Over/Under"
      : "Record an Assist (Yes)";
  return (
    PLAYER_SECTION_LABELS[baseName] ??
    toTitleLabel(baseName.replace(/^player_/, ""))
  );
}

// A trigger's prop_stem names the stat field, with or without the trailing
// over/under + point/price suffix depending on the market — strip both so it
// lines up with the section base names built above (sectionName / baseName).
// Trigger-only aliases: triggers on these stems belong to another section's
// odds. Kept apart from PLAYER_SECTION_ALIASES so the odds themselves aren't merged.
export const PLAYER_TRIGGER_STEM_ALIASES: Record<string, string> = {
  // "Batting Runs" odds are hidden (see HIDDEN_PLAYER_SECTION_RE) since they
  // duplicate Batting → Runs, so its triggers attach there.
  player_batting_runs: "player_points",
};

export function normalizePlayerStem(stem: string): string {
  const base = stem
    // "_no_price" is the No side of a combined Yes/No section (see PlayerOddsSection).
    .replace(/_no_price$/, "")
    .replace(/_point$|_price$|_lines$/, "")
    .replace(/_(over|under)$/, "");
  const aliased = PLAYER_SECTION_ALIASES[base] ?? base;
  return PLAYER_TRIGGER_STEM_ALIASES[aliased] ?? aliased;
}

export function triggersForPlayerSection(
  triggers: PropTrigger[],
  sectionBase: string,
  playerId: string,
): PropTrigger[] {
  return triggers.filter(
    (t) =>
      t.player_id === playerId &&
      !!t.prop_stem &&
      normalizePlayerStem(t.prop_stem) === sectionBase,
  );
}

export const SIDE_SUFFIX_RE = /_(over|under)_(point|price|lines)$/;
export const SIMPLE_SIDE_RE = /_(over|under)$/;

// Player prop sections where the line value is fixed/meaningless — show price only
export const PRICE_ONLY_PLAYER_SECTIONS = new Set([
  "player_assists",
  "player_shots_in_each_half",
  "player_shots_on_target_in_each_half",
  "player_to_score_or_assist",
]);

// Player prop sections to skip entirely for a given sport (e.g. NFL kicker
// field goals — not useful enough to surface as a group).
export const HIDDEN_NFL_PLAYER_SECTION_PREFIXES = [
  "player_field_goals_made",
  "player_points",
];

// Baseball "Batting Runs" duplicates the "Runs" (player_points) market, so it
// is not shown as its own section (matches the bare name and its _over/_under
// and _point/_price/_lines variants, which stay attached ahead of ":playerId").
export const HIDDEN_PLAYER_SECTION_RE =
  /^player_batting_runs(_(over|under))?(_(point|price|lines))?$/;

// Player prop sections hidden for now, in every sport.
export const HIDDEN_PLAYER_SECTION_PREFIXES = ["player_any_blocks_steals"];

// Section bases that should be merged from separate _over/_under simple sections
export const PLAYER_OVER_UNDER_PAIRS = new Set([
  "player_batting_hits",
  "player_batting_strikeouts",
  "player_strikeouts",
]);

export function getLineSectionInfo(
  sectionName: string,
): { baseName: string; side: string } | null {
  const m = sectionName.match(SIDE_SUFFIX_RE);
  if (m) return { baseName: sectionName.slice(0, -m[0].length), side: m[1] };
  const sm = sectionName.match(SIMPLE_SIDE_RE);
  if (sm) {
    const base = sectionName.slice(0, -sm[0].length);
    if (PLAYER_OVER_UNDER_PAIRS.has(base))
      return { baseName: base, side: sm[1] };
  }
  return null;
}
// ── Main Odds grouped section ────────────────────────────────────────────────

export type SubgroupField = {
  label: string;
  display: string;
  key: string;
  // The real, directly-fetchable chart key for this field (e.g. "away_spread_point").
  // Distinct from `key` because `key` is sometimes a bare base or a synthetic
  // per-alt-line id used only for React list identity, not an actual API field.
  chartKey?: string;
  isAlt?: boolean;
};

export type SubgroupDef = {
  key: string;
  label: string;
  getFields: (entry: OddsEntry) => SubgroupField[];
};

export function formatPoint(val: unknown): string {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return n % 1 === 0 ? `${n}.0` : String(n);
}

export interface LineEntry {
  point: number;
  price: number;
  is_main_line: boolean;
}

export function processLinesArray(
  linesVal: unknown,
  mainLabel: string,
  keyBase: string,
  chartKey?: string,
): SubgroupField[] {
  if (!Array.isArray(linesVal) || linesVal.length === 0) return [];
  const lines = linesVal as LineEntry[];
  const sorted = [...lines].sort((a, b) => a.point - b.point);
  const showSuffix =
    lines.some((l) => l.is_main_line) && lines.some((l) => !l.is_main_line);
  return sorted.flatMap((line, i) => {
    const p = formatPoint(line.point);
    const pr = formatOdds(line.price);
    const display = [p, pr !== "-" ? `(${pr})` : ""].filter(Boolean).join(" ");
    if (!display) return [];
    const label = showSuffix
      ? mainLabel
        ? `${mainLabel} ${line.is_main_line ? "(main)" : "(alt)"}`
        : line.is_main_line
          ? "(main)"
          : "(alt)"
      : mainLabel;
    return [
      {
        label,
        display,
        key: `${keyBase}_line_${i}`,
        chartKey,
        isAlt: !line.is_main_line,
      },
    ];
  });
}

// Reconciles a "_lines" alt-line array with its flat "_point"/"_price"
// siblings when both exist for the same market (different sportsbooks
// sometimes report the same stat under different field-name prefixes, one
// with the richer list and one with just a flat value). A list entry flagged
// is_main_line wins outright — the flat value is redundant with it, so it's
// ignored. A list present but missing a flagged main line gets the flat
// point/price merged in as the main line. Returns null (not []) when there's
// no usable list at all, so the caller knows to fall back to flat rendering.
export function resolveLineFields(
  linesVal: unknown,
  pointVal: unknown,
  priceVal: unknown,
  mainLabel: string,
  keyBase: string,
  chartKey?: string,
): SubgroupField[] | null {
  if (!Array.isArray(linesVal) || linesVal.length === 0) return null;
  const lines = linesVal as LineEntry[];
  if (lines.some((l) => l.is_main_line)) {
    return processLinesArray(lines, mainLabel, keyBase, chartKey);
  }
  const pointNum = Number(pointVal);
  const priceNum = Number(priceVal);
  if (
    pointVal === undefined ||
    priceVal === undefined ||
    isNaN(pointNum) ||
    isNaN(priceNum)
  ) {
    return processLinesArray(lines, mainLabel, keyBase, chartKey);
  }
  return processLinesArray(
    [...lines, { point: pointNum, price: priceNum, is_main_line: true }],
    mainLabel,
    keyBase,
    chartKey,
  );
}

export const SUBGROUPS: SubgroupDef[] = [
  {
    key: "line",
    label: "Line",
    getFields: (entry) => {
      // Pair _point and _price by shared prefix (e.g. over_total, under_total)
      const prefixes: string[] = [];
      Object.keys(entry).forEach((k) => {
        if (k.endsWith("_lines")) return; // handled via lines array below
        if (/over|under|total|_line/.test(k.toLowerCase())) {
          const base = k.replace(/_point$|_price$/, "");
          if (!prefixes.includes(base)) prefixes.push(base);
        }
      });
      return prefixes.flatMap((base) => {
        const baseLabel = toTitleLabel(
          base.replace(/_total$/, "").replace(/_line$/, ""),
        );
        const point = entry[`${base}_point`];
        const price = entry[`${base}_price`];
        const chartKey =
          point !== undefined
            ? `${base}_point`
            : price !== undefined
              ? `${base}_price`
              : base;
        // A _lines array with a flagged main line wins outright; one missing
        // a flagged main gets the flat point/price merged in as the main
        // line instead (see resolveLineFields).
        const linesVal = entry[`${base}_lines`];
        const resolvedLines = resolveLineFields(
          linesVal,
          point,
          price,
          baseLabel,
          base,
          chartKey,
        );
        if (resolvedLines !== null) return resolvedLines;
        const parts: string[] = [];
        const p = formatPoint(point);
        if (p) parts.push(p);
        const pr = formatOdds(price);
        if (pr !== "-") parts.push(`(${pr})`);
        if (parts.length === 0) return [];
        return [
          { label: baseLabel, display: parts.join(" "), key: base, chartKey },
        ];
      });
    },
  },
  {
    key: "moneyline",
    label: "Moneyline",
    getFields: (entry) =>
      Object.keys(entry)
        .filter((f) => /outcome|moneyline|_ml/.test(f.toLowerCase()))
        .flatMap((f) => {
          const val = entry[f];
          if (val === null || val === undefined || val === "") return [];
          const label = f.toLowerCase().includes("outcome")
            ? toTitleLabel(f.split("_outcome")[0])
            : toTitleLabel(f);
          return [{ label, display: formatOdds(val), key: f, chartKey: f }];
        }),
  },
  {
    key: "spread",
    label: "Spread",
    getFields: (entry) => {
      const prefixes: string[] = [];
      Object.keys(entry).forEach((k) => {
        if (k.endsWith("_lines")) return; // handled via lines array below
        if (/spread|handicap/.test(k.toLowerCase())) {
          const base = k.replace(/_point$|_price$/, "");
          if (!prefixes.includes(base)) prefixes.push(base);
        }
      });
      return prefixes.flatMap((base) => {
        const baseLabel = toTitleLabel(
          base.replace(/_spread$/, "").replace(/_handicap$/, ""),
        );
        const point = entry[`${base}_point`];
        const price = entry[`${base}_price`];
        const chartKey =
          point !== undefined ? `${base}_point` : `${base}_price`;
        // A _lines array with a flagged main line wins outright; one missing
        // a flagged main gets the flat point/price merged in as the main
        // line instead (see resolveLineFields).
        const linesVal = entry[`${base}_lines`];
        const resolvedLines = resolveLineFields(
          linesVal,
          point,
          price,
          baseLabel,
          base,
          chartKey,
        );
        if (resolvedLines !== null) return resolvedLines;
        // If no _point/_price/_lines pattern, fall back to the raw field
        if (point === undefined && price === undefined) {
          const val = entry[base];
          if (val === null || val === undefined || val === "") return [];
          return [
            {
              label: toTitleLabel(base),
              display: formatOdds(val),
              key: base,
              chartKey: base,
            },
          ];
        }
        const parts: string[] = [];
        const p = formatPoint(point);
        if (p) parts.push(p);
        const pr = formatOdds(price);
        if (pr !== "-") parts.push(`(${pr})`);
        if (parts.length === 0) return [];
        return [
          { label: baseLabel, display: parts.join(" "), key: base, chartKey },
        ];
      });
    },
  },
];
